/**
 * useAccountStatus Hook
 * React hook for managing account status and access control
 * Integrates with Stripe billing and company management
 */

import { useEffect, useCallback, useSyncExternalStore } from 'react'
import Parse from 'parse'
import { accountStatusService, AccountStatus, ActionPermissionResult } from '../services/AccountStatusService'
import authService from '../services/authService'
import { parseUserId } from '../utils/parseUserId'

export interface UseAccountStatusReturn {
  // Account status
  accountStatus: AccountStatus | null
  loading: boolean
  error: string | null
  
  // Permission checks
  canCreateRooms: boolean
  canJoinRooms: boolean
  canJoinCustomRooms: boolean
  canAccessDashboard: boolean
  canAccessCompanyManagement: boolean
  canAccessBilling: boolean
  canAccessProfile: boolean
  hasFullAccess: boolean
  isGlobalAdmin: boolean
  
  // Status information
  subscriptionStatus: string
  paymentStatus: string
  restrictions: string[]
  statusMessage: string
  
  // Action status
  needsAction: boolean
  actionType: 'payment' | 'subscription' | 'none'
  actionMessage: string
  
  // Methods
  refresh: () => Promise<void>
  checkPermission: (action: string, resource?: string) => Promise<ActionPermissionResult>
  clearCache: () => void
}

/** Same cadence as the service cache, so a poll tick and a cache expiry line up. */
export const ACCOUNT_STATUS_POLL_MS = 5 * 60 * 1000
/** A tab that comes back to the foreground refreshes only when its data is at least this old. */
export const ACCOUNT_STATUS_VISIBLE_MIN_AGE_MS = 5 * 60 * 1000
const ACCOUNT_STATUS_MAX_BACKOFF_MS = 30 * 60 * 1000
// AccountStatusService.getAccountStatus does not throw when the cloud call fails: it resolves with
// a restricted placeholder carrying this text, so the poller recognises a failure by it.
const ACCOUNT_STATUS_FAILURE_RESTRICTION = 'Failed to check account status'

interface AccountStatusSnapshot {
  /** The user the data belongs to; data is never shown to a different signed-in user. */
  uid: string | null
  accountStatus: AccountStatus | null
  loading: boolean
  error: string | null
  permissions: {
    canCreateRooms: boolean
    canJoinRooms: boolean
    canJoinCustomRooms: boolean
    canAccessDashboard: boolean
    canAccessCompanyManagement: boolean
    canAccessBilling: boolean
    canAccessProfile: boolean
    hasFullAccess: boolean
    isGlobalAdmin: boolean
  }
  statusInfo: {
    subscriptionStatus: string
    paymentStatus: string
    restrictions: string[]
    statusMessage: string
  }
  actionStatus: {
    needsAction: boolean
    actionType: 'payment' | 'subscription' | 'none'
    actionMessage: string
  }
}

const INITIAL_SNAPSHOT: AccountStatusSnapshot = {
  uid: null,
  accountStatus: null,
  loading: true,
  error: null,
  permissions: {
    canCreateRooms: false,
    canJoinRooms: false,
    canJoinCustomRooms: false,
    canAccessDashboard: false,
    canAccessCompanyManagement: false,
    canAccessBilling: false,
    canAccessProfile: true, // Profile access is always allowed
    hasFullAccess: false,
    isGlobalAdmin: false,
  },
  statusInfo: {
    subscriptionStatus: 'none',
    paymentStatus: 'none',
    restrictions: [],
    statusMessage: 'Loading...',
  },
  actionStatus: { needsAction: false, actionType: 'none', actionMessage: '' },
}

// Every Back4App-billed fetch of the account status comes from this one module-level state, so
// the number of mounted hook instances (5+ on the dashboard) does not multiply requests.
let snapshot: AccountStatusSnapshot = INITIAL_SNAPSHOT
const listeners = new Set<() => void>()
let inFlight: Promise<void> | null = null
let loadSeq = 0
let lastLoadedAt = 0
// The user the module-level load state above belongs to; see syncUser.
let stateUid: string | null = null
let failures = 0
let backoffUntil = 0
let pollSubscribers = 0
let pollTimer: ReturnType<typeof setInterval> | null = null

function publish(next: AccountStatusSnapshot): void {
  snapshot = next
  listeners.forEach((listener) => listener())
}

function subscribeStore(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): AccountStatusSnapshot {
  // Parse keeps the previous user's data in memory across a sign-out/sign-in; don't show it. A
  // snapshot with no uid (a result that finished while signed out) is never served to a signed-in
  // user either.
  if (snapshot.uid !== parseUserId(Parse)) return INITIAL_SNAPSHOT
  return snapshot
}

/**
 * Sign-out does not reload the page, so this module's state outlives the user. When the user
 * changes, the old load (and its timestamps and back-off) must not be joined, trusted or counted
 * against the new user.
 */
function syncUser(): void {
  const uid = parseUserId(Parse)
  if (uid === stateUid) return
  stateUid = uid
  loadSeq += 1
  inFlight = null
  lastLoadedAt = 0
  failures = 0
  backoffUntil = 0
}

// A tick fires a little before or after the nominal interval; without slack the tick that is
// meant to retry would land just inside the back-off and be skipped, doubling the wait.
const BACKOFF_SLACK_MS = 1000

/**
 * `startedAt` is when the failed load began, not when it failed: the next tick is one interval
 * after the one that started this load, so measuring from the failure (a slow, timed-out read)
 * would push the first retry from 5 min to 10 min.
 */
function noteFailure(startedAt: number): void {
  failures += 1
  // First retry one poll interval later (the next tick), then 10 min, 20 min, capped at 30.
  backoffUntil =
    startedAt +
    Math.min(ACCOUNT_STATUS_POLL_MS * 2 ** (failures - 1), ACCOUNT_STATUS_MAX_BACKOFF_MS)
}

async function runLoad(forceRefresh: boolean, seq: number): Promise<void> {
  const startedAt = Date.now()
  // Taken before the first await: the answer belongs to whoever asked, even if the session
  // changes while it is in flight.
  const startUid = parseUserId(Parse)
  const base: AccountStatusSnapshot =
    snapshot.uid === startUid ? snapshot : { ...INITIAL_SNAPSHOT, uid: startUid }
  const hasData = base.accountStatus !== null
  // A background refresh with data on screen must not flip every consumer to "loading": that
  // disables join buttons and hides restriction tooltips for the length of the request.
  if (!hasData || forceRefresh) publish({ ...base, loading: true, error: null })
  else if (base.error) publish({ ...base, error: null })

  const isStale = () => seq !== loadSeq || parseUserId(Parse) !== startUid
  try {
    const sessionUser = await authService.waitForSessionCheck()
    const currentUser = Parse.User.current()
    if (!sessionUser || !currentUser) {
      if (!isStale()) publish({ ...base, loading: false })
      return
    }

    const status = await accountStatusService.getAccountStatus(forceRefresh)
    if (status.restrictions?.includes(ACCOUNT_STATUS_FAILURE_RESTRICTION) && hasData) {
      // A failed read answers with a restricted placeholder; showing it over a good snapshot would
      // lock the user out of guarded routes until the back-off ends. Keep what was shown.
      if (isStale()) return
      publish({ ...base, loading: false, error: null })
      noteFailure(startedAt) // lastLoadedAt stays as it was
      return
    }
    const failedRead = !!status.restrictions?.includes(ACCOUNT_STATUS_FAILURE_RESTRICTION)
    // getStatusMessage() and needsAction() each call getAccountStatus() again, and a failed read
    // is never cached, so on the placeholder they would be two more billed calls (each with a
    // 10 s timeout) that only repeat what `status` already says.
    const statusMessage = failedRead
      ? ACCOUNT_STATUS_FAILURE_RESTRICTION
      : await accountStatusService.getStatusMessage()
    const action = failedRead
      ? { needsAction: false, actionType: 'none' as const, message: 'No action needed' }
      : await accountStatusService.needsAction()
    if (isStale()) {
      // The user changed mid-load: this answer is for someone else. Whoever is signed in now
      // still needs their own load, and nothing else will start it until the next tick.
      if (seq === loadSeq && parseUserId(Parse) !== null) void loadShared(false)
      return
    }

    publish({
      uid: startUid,
      accountStatus: status,
      loading: false,
      error: null,
      permissions: {
        canCreateRooms: status.canCreateRooms,
        canJoinRooms: status.canJoinRooms,
        canJoinCustomRooms:
          (status as any).canJoinCustomRooms ??
          (status.subscriptionStatus === 'active' || status.subscriptionStatus === 'trialing'),
        canAccessDashboard: status.canAccessDashboard,
        canAccessCompanyManagement: status.canAccessCompanyManagement,
        canAccessBilling: (status as any).canAccessBilling ?? false,
        canAccessProfile: true, // Always allowed
        hasFullAccess: status.hasFullAccess,
        isGlobalAdmin: status.isGlobalAdmin,
      },
      statusInfo: {
        subscriptionStatus: status.subscriptionStatus,
        paymentStatus: status.paymentStatus,
        restrictions: status.restrictions,
        statusMessage,
      },
      // Global Admins never see "action required".
      actionStatus: status.isGlobalAdmin
        ? { needsAction: false, actionType: 'none', actionMessage: 'No action needed - Global Admin' }
        : {
            needsAction: action.needsAction,
            actionType: action.actionType,
            actionMessage: action.message,
          },
    })

    if (failedRead) {
      noteFailure(startedAt) // the failed answer is not a fresh read: lastLoadedAt stays as it was
    } else {
      failures = 0
      backoffUntil = 0
      lastLoadedAt = Date.now()
    }
  } catch (err) {
    console.error('Error loading account status:', err)
    if (parseUserId(Parse) !== startUid) return // another user's failure must not back this one off
    noteFailure(startedAt)
    if (seq === loadSeq) {
      publish({
        ...(snapshot.uid === startUid ? snapshot : base),
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load account status',
      })
    }
  }
}

/** Concurrent callers share one load; a forced refresh never joins an older one. */
function loadShared(forceRefresh: boolean): Promise<void> {
  syncUser()
  if (!forceRefresh && inFlight) return inFlight
  const seq = ++loadSeq
  const request: Promise<void> = runLoad(forceRefresh, seq).finally(() => {
    if (inFlight === request) inFlight = null
  })
  inFlight = request
  return request
}

/** Poll ticks and visibility refreshes: never while hidden, in flight or backing off. */
function maybeBackgroundLoad(minAgeMs: number): void {
  if (typeof document !== 'undefined' && document.hidden) return
  syncUser()
  if (inFlight) return
  if (Date.now() + BACKOFF_SLACK_MS < backoffUntil) return
  if (Date.now() - lastLoadedAt < minAgeMs) return
  void loadShared(false)
}

function onVisibilityChange(): void {
  maybeBackgroundLoad(ACCOUNT_STATUS_VISIBLE_MIN_AGE_MS)
}

function retainPoller(): () => void {
  pollSubscribers += 1
  if (pollSubscribers === 1) {
    pollTimer = setInterval(() => maybeBackgroundLoad(0), ACCOUNT_STATUS_POLL_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)
  }
  return () => {
    pollSubscribers -= 1
    if (pollSubscribers === 0) {
      if (pollTimer) clearInterval(pollTimer)
      pollTimer = null
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }
}

/** Test hook: the state above is module-level and would leak between tests. */
export function resetAccountStatusPollerForTests(): void {
  if (pollTimer) clearInterval(pollTimer)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  pollTimer = null
  pollSubscribers = 0
  snapshot = INITIAL_SNAPSHOT
  inFlight = null
  loadSeq = 0
  lastLoadedAt = 0
  stateUid = null
  failures = 0
  backoffUntil = 0
}

export const useAccountStatus = (autoRefresh: boolean = true): UseAccountStatusReturn => {
  const { accountStatus, loading, error, permissions, statusInfo, actionStatus } =
    useSyncExternalStore(subscribeStore, getSnapshot, getSnapshot)

  const refresh = useCallback(async () => {
    await loadShared(true)
  }, [])

  const checkPermission = useCallback(async (action: string, resource?: string): Promise<ActionPermissionResult> => {
    try {
      return await accountStatusService.checkActionPermission(action, resource)
    } catch (err) {
      console.error('Error checking permission:', err)
      return {
        allowed: false,
        reason: 'Failed to check permission',
        accountStatus: {
          subscriptionStatus: 'none',
          paymentStatus: 'none',
          restrictions: ['Permission check failed'],
          company: null,
          userRole: null
        }
      }
    }
  }, [])

  const clearCache = useCallback(() => {
    accountStatusService.clearCache()
  }, [])

  // Load account status after boot-time session gate settles
  useEffect(() => {
    let cancelled = false

    const run = async () => {
      await authService.waitForSessionCheck()
      if (cancelled) return
      await loadShared(false)
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [])

  // Auto-refresh: one poller per tab, however many hook instances ask for it
  useEffect(() => {
    if (!autoRefresh) return
    return retainPoller()
  }, [autoRefresh])

  return {
    // Account status
    accountStatus,
    loading,
    error,
    
    // Permission checks
    canCreateRooms: permissions.canCreateRooms,
    canJoinRooms: permissions.canJoinRooms,
    canJoinCustomRooms: permissions.canJoinCustomRooms,
    canAccessDashboard: permissions.canAccessDashboard,
    canAccessCompanyManagement: permissions.canAccessCompanyManagement,
    canAccessBilling: permissions.canAccessBilling,
    canAccessProfile: permissions.canAccessProfile,
    hasFullAccess: permissions.hasFullAccess,
    isGlobalAdmin: permissions.isGlobalAdmin,
    
    // Status information
    subscriptionStatus: statusInfo.subscriptionStatus,
    paymentStatus: statusInfo.paymentStatus,
    restrictions: statusInfo.restrictions,
    statusMessage: statusInfo.statusMessage,
    
    // Action status
    needsAction: actionStatus.needsAction,
    actionType: actionStatus.actionType,
    actionMessage: actionStatus.actionMessage,
    
    // Methods
    refresh,
    checkPermission,
    clearCache
  }
}

// Convenience hooks for specific permissions
export const useRoomCreationPermission = () => {
  const { canCreateRooms, loading, error, refresh } = useAccountStatus()
  return { canCreateRooms, loading, error, refresh }
}

export const useRoomJoiningPermission = () => {
  const { canJoinRooms, loading, error, refresh } = useAccountStatus()
  return { canJoinRooms, loading, error, refresh }
}

export const useCustomRoomJoiningPermission = () => {
  const { canJoinCustomRooms, loading, error, refresh } = useAccountStatus()
  return { canJoinCustomRooms, loading, error, refresh }
}

export const useDashboardAccess = () => {
  const { canAccessDashboard, loading, error, refresh } = useAccountStatus()
  return { canAccessDashboard, loading, error, refresh }
}

export const useCompanyManagementAccess = () => {
  const { canAccessCompanyManagement, loading, error, refresh } = useAccountStatus()
  return { canAccessCompanyManagement, loading, error, refresh }
}

export const useBillingAccess = () => {
  const { canAccessBilling, loading, error, refresh } = useAccountStatus()
  return { canAccessBilling, loading, error, refresh }
}

export const useGlobalAdminStatus = () => {
  const { isGlobalAdmin, loading, error, refresh } = useAccountStatus()
  return { isGlobalAdmin, loading, error, refresh }
}

export const useSubscriptionStatus = () => {
  const { 
    subscriptionStatus, 
    paymentStatus, 
    restrictions, 
    statusMessage, 
    needsAction, 
    actionType, 
    actionMessage,
    loading, 
    error, 
    refresh 
  } = useAccountStatus()
  
  return {
    subscriptionStatus,
    paymentStatus,
    restrictions,
    statusMessage,
    needsAction,
    actionType,
    actionMessage,
    loading,
    error,
    refresh
  }
}
